import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    Collapse,
    FormControl,
    FormHelperText,
    Button,
} from '@mui/material';
import FormTextField from '../../../shared/FormTextField';
import FormCheckbox from '../../../shared/FormCheckbox';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateRsvpConfigMutation } from '../../../../app/Slices/SevenDayApiSlice';
import { rsvpConfigSchema } from '../ReservationRequest/Shared/schema';
import toast from '../../../shared/Toast';

type RsvpConfig = z.infer<typeof rsvpConfigSchema>;

export const RsvpConfig = ({
    bookingId,
    config,
    reservationStatus,
}: {
    bookingId: number;
    config: RsvpConfig;
    reservationStatus: string;
}) => {
    const [updateConfig] = useUpdateRsvpConfigMutation();

    const methods = useForm<RsvpConfig>({
        resolver: zodResolver(rsvpConfigSchema),
        defaultValues: {
            rsvpEnabled: false,
            rsvpMessage: '',
            autoRemindersEnabled: false,
            reminderDaysBeforeEvent: 0,
        },
    });
    const {
        reset,
        control,
        formState: { isDirty },
        handleSubmit,
        watch,
    } = methods;

    useEffect(() => {
        if (config) {
            reset(config);
        }
    }, [config, reset]);

    const onSubmit = async (data: RsvpConfig) => {
        try {
            await updateConfig({ bookingId, config: data }).unwrap();
            toast.success('RSVP config saved successfully!');
        } catch {
            toast.error('Error updating RSVP config.');
        }
    };

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'custom.lightGrey',
                backgroundColor: 'background.paper',
                p: 2,
            }}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography variant='h6' mb={2}>
                        RSVP Settings
                    </Typography>
                    <FormControl>
                        <Box display='flex' alignItems='center' sx={{ m: 0 }}>
                            <FormCheckbox
                                control={control}
                                name='rsvpEnabled'
                            />
                            <Typography
                                variant='caption'
                                fontWeight={550}
                                sx={{ ml: 1 }}
                                component='label'
                            >
                                Would you like to request RSVPs?
                            </Typography>
                        </Box>
                        {reservationStatus === 'pending' && (
                            <FormHelperText>
                                RSVP requests will be sent once the reservation
                                has been approved by the host.
                            </FormHelperText>
                        )}
                    </FormControl>
                    <Collapse in={watch('rsvpEnabled')}>
                        <Box sx={{ mt: 3 }}>
                            <Typography variant='subtitle2' sx={{ mb: 2 }}>
                                RSVP Message
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <FormTextField
                                    control={control}
                                    name='rsvpMessage'
                                    fullWidth
                                    multiline
                                    rows={6}
                                />
                                <FormHelperText>
                                    This message will be sent to all attendees
                                </FormHelperText>
                            </Box>
                            <Box sx={{ mt: 3 }}>
                                <FormCheckbox
                                    control={control}
                                    name='autoRemindersEnabled'
                                />
                                <Typography
                                    variant='caption'
                                    fontWeight={550}
                                    sx={{ ml: 1 }}
                                    component='label'
                                >
                                    Enable automatic reminders
                                </Typography>

                                {watch('autoRemindersEnabled') && (
                                    <Box sx={{ mt: 2, ml: 4 }}>
                                        <FormTextField
                                            control={control}
                                            name='reminderDaysBeforeEvent'
                                            type='number'
                                            label='Days before event'
                                            inputProps={{ min: 1, max: 30 }}
                                            sx={{ width: 200 }}
                                            helperText='Send reminders this many days before the event'
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                    <Box display='flex' justifyContent='flex-end' gap={2}>
                        <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            disabled={!isDirty}
                        >
                            Save
                        </Button>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
};
